import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Assignee,
  CallTaskChanges,
  CallTaskDetail,
  CallTaskOutcome,
  isConflict,
  isNotFound,
  lifecycleAPI,
  lifecycleErrorMessage,
} from '@/services/lifecycleAPI';
import {
  lifecycleFailureOutcome,
  LifecycleSurface,
  trackLifecycleActionTaken,
} from '@/util/vexo';

import {
  CallTaskForm,
  changedFields,
  formFromTask,
  rolesError,
} from './callTaskForm';

type LoadState = 'loading' | 'ready' | 'notFound' | 'error';

const EMPTY_FORM: CallTaskForm = {
  outcome: '',
  assignee: '',
  roles: '',
  days: [],
  notes: '',
};

type Options = {
  taskId: number;
  onChanged: () => void;
  onCompleted: (message: string) => void;
  /** The screen the dialog is on; labels the analytics event only. */
  surface?: LifecycleSurface;
};

const useCallTaskDialog = ({
  taskId,
  onChanged,
  onCompleted,
  surface = 'call_tasks',
}: Options) => {
  const [task, setTask] = useState<CallTaskDetail | null>(null);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [assigneesError, setAssigneesError] = useState<string | null>(null);
  const [form, setForm] = useState<CallTaskForm>(EMPTY_FORM);
  const [load, setLoad] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saved, setSaved] = useState(false);
  const touched = useRef(new Set<keyof CallTaskForm>());

  const fetchTask = useCallback(async () => {
    setLoad('loading');
    setLoadError(null);
    try {
      const detail = await lifecycleAPI.getCallTask(taskId);
      setTask(detail);
      setForm((current) => {
        const loaded = formFromTask(detail);
        touched.current.forEach((key) => {
          (loaded as Record<string, unknown>)[key] = current[key];
        });
        return loaded;
      });
      setLoad('ready');
    } catch (err) {
      if (isNotFound(err)) {
        setLoad('notFound');
        return;
      }
      setLoadError(lifecycleErrorMessage(err, 'Could not load the call task.'));
      setLoad('error');
    }
  }, [taskId]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  useEffect(() => {
    let active = true;
    lifecycleAPI
      .listAssignees()
      .then((list) => {
        if (active) setAssignees(list);
      })
      .catch((err) => {
        if (active) {
          setAssigneesError(
            lifecycleErrorMessage(err, 'Could not load the admins.'),
          );
        }
      });
    return () => {
      active = false;
    };
  }, [taskId]);

  const setField = useCallback(
    <K extends keyof CallTaskForm>(key: K, value: CallTaskForm[K]) => {
      touched.current.add(key);
      setForm((current) => ({ ...current, [key]: value }));
      setSaved(false);
      setError(null);
    },
    [],
  );

  const submit = async (changes: CallTaskChanges, completing: boolean) => {
    if (!task) return;
    setBusy(true);
    setError(null);
    setConflict(false);
    setSaved(false);
    const track = (outcome: 'success' | 'conflict' | 'error') =>
      trackLifecycleActionTaken({
        action: completing ? 'call_log' : 'call_save',
        outcome,
        surface,
        journey_key: task.journey_key,
        step_key: task.step_key,
        task_id: task.id,
        call_outcome: completing ? (changes.outcome ?? null) : null,
        // Names only: never the notes or roles typed.
        fields: Object.keys(changes).filter((key) => key !== 'status'),
      });
    try {
      const updated = await lifecycleAPI.updateCallTask(
        task.id,
        task.version ? { ...changes, version: task.version } : changes,
      );
      track('success');
      onChanged();
      if (completing) {
        onCompleted('Call logged.');
        return;
      }
      const merged = { ...task, ...updated, version: updated.version ?? null };
      touched.current.clear();
      setTask(merged);
      // Save never sends the outcome (only Complete does), so the saved task
      // has none: keep the one picked, or Complete went disabled (review D-05).
      setForm((current) => ({
        ...formFromTask(merged),
        outcome: current.outcome,
      }));
      setSaved(true);
    } catch (err) {
      track(lifecycleFailureOutcome(err));
      setConflict(isConflict(err));
      // A 409 means someone else changed the task, so the list behind this
      // dialog is stale whether or not the admin clicks Reload (review D-02).
      if (isConflict(err)) onChanged();
      setError(lifecycleErrorMessage(err, 'Could not update the call task.'));
    } finally {
      setBusy(false);
    }
  };

  const changes = task ? changedFields(task, form) : {};
  const hasChanges = task
    ? Object.keys(changes).length > 0
    : touched.current.size > 0;
  const formError = rolesError(form.roles);

  const save = () => submit(changes, false);

  const complete = () =>
    submit(
      {
        status: 'completed',
        outcome: form.outcome as CallTaskOutcome,
        ...changes,
      },
      true,
    );

  // Reload after a 409 shows what someone else saved: only a first load that
  // is still arriving keeps what the admin typed.
  const reload = async () => {
    touched.current.clear();
    setError(null);
    setConflict(false);
    setSaved(false);
    onChanged();
    await fetchTask();
  };

  return {
    task,
    assignees,
    assigneesError,
    form,
    setField,
    load,
    loadError,
    retryLoad: fetchTask,
    busy,
    error,
    conflict,
    saved,
    hasChanges,
    formError,
    save,
    complete,
    reload,
  };
};

export default useCallTaskDialog;
